package com.anjuros.website.redesign.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.anjuros.website.components.language.LocalText
import com.anjuros.website.redesign.styles.RdHeaderActionsStyle
import com.anjuros.website.redesign.styles.RdHeaderBarStyle
import com.anjuros.website.redesign.styles.RdHeaderCtaStyle
import com.anjuros.website.redesign.styles.RdHeaderStyle
import com.anjuros.website.redesign.styles.RdLogoStyle
import com.anjuros.website.redesign.styles.RdMenuButtonStyle
import com.anjuros.website.redesign.styles.RdNavCtaStyle
import com.anjuros.website.redesign.styles.RdNavLinkCurrentStyle
import com.anjuros.website.redesign.styles.RdNavLinkStyle
import com.anjuros.website.redesign.styles.RdNavOpenStyle
import com.anjuros.website.redesign.styles.RdNavStyle
import com.anjuros.website.redesign.styles.RdWrapStyle
import com.anjuros.website.redesign.util.RdIcons
import com.anjuros.website.redesign.util.RdImage
import com.anjuros.website.redesign.util.RdRoute
import com.anjuros.website.redesign.util.RdText
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Header
import org.jetbrains.compose.web.dom.Nav
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

data class RdNavItem(val text: RdText, val path: String, val current: Boolean = false)

/** Main navigation. Every item opens its own page, even when the current page has a section on the same topic. */
fun rdNavItems(currentPath: String? = null): List<RdNavItem> = listOf(
    RdNavItem(RdText.NAV_DESIGNS, RdRoute.DESIGNS),
    RdNavItem(RdText.NAV_TRADES, RdRoute.TRADES),
    RdNavItem(RdText.NAV_PORTAL, RdRoute.PORTAL),
    RdNavItem(RdText.NAV_SEO, RdRoute.SEO),
    RdNavItem(RdText.NAV_PRICING, RdRoute.PRICING),
    RdNavItem(RdText.NAV_ABOUT, RdRoute.ABOUT),
    RdNavItem(RdText.NAV_FAQ, RdRoute.FAQ),
    RdNavItem(RdText.NAV_BLOG, RdRoute.BLOG),
).map { it.copy(current = it.path == currentPath) }

@Composable
fun SiteHeader(items: List<RdNavItem>, ctaPath: String = RdRoute.CONTACT) {
    var menuOpen by remember { mutableStateOf(false) }
    val navLabel = LocalText(RdText.NAV_MAIN_LABEL)

    Header(RdHeaderStyle.toModifier().toAttrs()) {
        Div(RdWrapStyle.toModifier().then(RdHeaderBarStyle.toModifier()).toAttrs()) {
            RdHomeLink(RdLogoStyle.toModifier()) {
                RdPicture(RdImage.LOGO, LocalText(RdText.LOGO_ALT), lazy = false)
            }

            Nav(
                RdNavStyle.toModifier()
                    .thenIf(menuOpen, RdNavOpenStyle.toModifier())
                    .id("site-nav")
                    .attrsModifier { attr("aria-label", navLabel) }
                    .toAttrs()
            ) {
                items.forEach { item ->
                    RdLink(
                        item.path,
                        RdNavLinkStyle.toModifier()
                            .thenIf(item.current, RdNavLinkCurrentStyle.toModifier().attrsModifier { attr("aria-current", "page") })
                            .onClick { menuOpen = false }
                    ) { Text(LocalText(item.text)) }
                }
                RdLink(ctaPath, RdNavCtaStyle.toModifier().onClick { menuOpen = false }) {
                    Text(LocalText(RdText.NAV_GET_STARTED_ARROW))
                }
            }

            Div(RdHeaderActionsStyle.toModifier().toAttrs()) {
                Button(
                    RdMenuButtonStyle.toModifier()
                        .attrsModifier {
                            attr("type", "button")
                            attr("aria-controls", "site-nav")
                            attr("aria-expanded", menuOpen.toString())
                        }
                        .onClick { menuOpen = !menuOpen }
                        .toAttrs()
                ) {
                    if (menuOpen) RdIcons.Close() else RdIcons.Menu()
                    Span { Text(LocalText(if (menuOpen) RdText.NAV_CLOSE else RdText.NAV_MENU)) }
                }
                Div(RdHeaderCtaStyle.toModifier().toAttrs()) {
                    RdPrimaryButton(ctaPath, RdText.NAV_GET_STARTED)
                }
            }
        }
    }
}
